import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { crmQueryKeys } from '@/lib/crm/query/queries';
import {
  type HiddenMeetings,
  hiddenMeetingsQueryKey,
} from '@/lib/dashboard/hiddenMeetings';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

export interface UseUndismissMeetingResult {
  /** Restores a meeting to the current user's dashboard via the self-only
   * quickfetch undismiss endpoint. A call for an entity that already has an
   * undismiss in flight is a no-op. */
  undismissMeeting: (meetingEntityId: string) => Promise<void>;
  /** Whether an undismiss request for this specific entity is currently in flight. */
  isUndismissing: (meetingEntityId: string) => boolean;
}

interface UndismissResponse {
  dismissed: boolean;
  entity_id: string;
  hidden_meetings?: {
    series_ids: string[];
    meeting_entity_ids: string[];
  };
}

/**
 * Returns `{ undismissMeeting, isUndismissing }` for restoring a meeting to the
 * current user's dashboard.
 *
 * On success the hidden-meetings cache is seeded from the server response (which
 * is the authoritative post-undismiss hidden set), collapsing the "Show on
 * dashboard" button immediately without a separate server round-trip. The
 * meetings list cache is then invalidated to refresh the dashboard.
 *
 * No optimistic pre-write: unlike a dismiss (which removes a known row and is
 * trivially reversible), an undismiss would have to INSERT a row whose position
 * depends on `ahoy_start_time`, into a list that may be date-filtered such that
 * the meeting doesn't belong in it at all. Applying only once the server
 * confirms avoids inventing a placement the next refetch contradicts.
 */
export function useUndismissMeeting(orgId: number): UseUndismissMeetingResult {
  const queryClient = useQueryClient();
  const inFlightRef = useRef<Set<string>>(new Set());
  // Ref mutations alone don't trigger a re-render, but callers need
  // isUndismissing() to reflect the in-flight state immediately for the
  // button's disabled prop — this state bump forces that re-render.
  const [, forceRender] = useState(0);

  const isUndismissing = useCallback(
    (meetingEntityId: string) => inFlightRef.current.has(meetingEntityId),
    []
  );

  const undismissMeeting = useCallback(
    async (meetingEntityId: string) => {
      if (!orgId) {
        return;
      }

      if (inFlightRef.current.has(meetingEntityId)) {
        logger.debug('Undismiss meeting: suppressed duplicate in-flight call', {
          meetingEntityId,
          orgId,
        });
        return;
      }
      inFlightRef.current.add(meetingEntityId);
      forceRender((n: number) => n + 1);

      try {
        const response = await apiPost<UndismissResponse>(
          `/api/orgs/${orgId}/quickfetch/undismiss/`,
          { entity_id: meetingEntityId }
        );
        if (response.hidden_meetings) {
          queryClient.setQueryData<HiddenMeetings>(
            hiddenMeetingsQueryKey(orgId),
            {
              seriesIds: response.hidden_meetings.series_ids,
              meetingEntityIds: response.hidden_meetings.meeting_entity_ids,
            }
          );
        }
      } finally {
        inFlightRef.current.delete(meetingEntityId);
        forceRender((n: number) => n + 1);
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(orgId, 'ahoy_meeting'),
        });
      }
    },
    [orgId, queryClient]
  );

  return { undismissMeeting, isUndismissing };
}
